import { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis
} from 'recharts';

const sectionTitleSx = {
    fontWeight: 700,
    pb: 1,
    borderBottom: '2px solid #BDBDBD',
    color: '#171717'
};

const cardSx = {
    p: { xs: 1.8, md: 2.2 },
    borderRadius: '20px',
    bgcolor: '#FFFFFF',
    boxShadow: 'none',
    height: '100%'
};

const softInnerCardSx = {
    p: 1.25,
    borderRadius: '14px',
    bgcolor: '#ECECEC',
    boxShadow: 'none'
};

const panelInputSx = {
    '& .MuiInputBase-root': {
        bgcolor: '#FFFFFF',
        borderRadius: '10px'
    }
};

const metricTableHeaderSx = {
    px: 2.2,
    py: 1.1,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr 1fr 0.9fr 0.9fr 0.9fr 1.2fr',
    gap: 1.2,
    bgcolor: '#3D3D3D',
    color: '#fff',
    borderRadius: '10px',
    boxShadow: 'none',
    textAlign: 'center'
};

const metricTableRowSx = {
    px: 2.2,
    py: 1.35,
    minHeight: 68,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr 1fr 0.9fr 0.9fr 0.9fr 1.2fr',
    gap: 1.2,
    bgcolor: '#ECECEC',
    borderRadius: '12px',
    alignItems: 'center',
    boxShadow: 'none'
};

const noteTableHeaderSx = {
    px: 2.2,
    py: 1.1,
    display: 'grid',
    gridTemplateColumns: '1.1fr 1.5fr 1.6fr 0.7fr',
    gap: 1.2,
    bgcolor: '#3D3D3D',
    color: '#fff',
    borderRadius: '10px',
    boxShadow: 'none'
};

const noteTableRowSx = {
    px: 2.2,
    py: 1.35,
    minHeight: 68,
    display: 'grid',
    gridTemplateColumns: '1.1fr 1.5fr 1.6fr 0.7fr',
    gap: 1.2,
    bgcolor: '#ECECEC',
    borderRadius: '12px',
    alignItems: 'center',
    boxShadow: 'none'
};

const actionButtonSx = {
    bgcolor: '#3D3D3D',
    color: '#fff',
    width: 34,
    height: 34,
    minWidth: 34,
    '&:hover': {
        bgcolor: '#3D3D3D'
    }
};

const appointmentHeaderSx = {
    px: 2.2,
    py: 1.1,
    display: 'grid',
    gridTemplateColumns: '1.1fr 1fr 1fr 1.25fr 132px',
    gap: 1.2,
    bgcolor: '#3D3D3D',
    color: '#fff',
    borderRadius: '10px',
    boxShadow: 'none'
};

const appointmentRowSx = {
    px: 2.2,
    py: 1.35,
    minHeight: 74,
    display: 'grid',
    gridTemplateColumns: '1.1fr 1fr 1fr 1.25fr 132px',
    gap: 1.2,
    bgcolor: '#ECECEC',
    borderRadius: '12px',
    alignItems: 'center',
    boxShadow: 'none'
};

const toPtDateTime = (isoTs) => {
    if (!isoTs) return '--';
    const date = new Date(isoTs);
    if (Number.isNaN(date.getTime())) return isoTs;
    return date.toLocaleString('pt-PT');
};

const formatEvaluationStamp = (isoTs) => {
    if (!isoTs) return '- | -';
    const date = new Date(isoTs);
    if (Number.isNaN(date.getTime())) return '- | -';

    const day = date.getDate();
    const month = date.getMonth() + 1;
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${day}/${month} | ${hours}:${minutes}`;
};

const normalizeMoodState = (value) =>
    String(value || '')
        .trim()
        .toLowerCase();

const moodStateToScore = (state) => {
    const normalized = normalizeMoodState(state);

    if (normalized === 'excelente') return 7;
    if (normalized === 'muito bem') return 6;
    if (normalized === 'bem') return 5;
    if (normalized === 'calmo') return 5;
    if (normalized === 'razoavel') return 4;
    if (normalized === 'triste') return 2;
    if (normalized === 'mal') return 2;

    return null;
};

const formatMoodStateLabel = (value) => {
    const text = String(value || '').trim();
    if (!text) return 'Sem nivel registado';
    return text.charAt(0).toUpperCase() + text.slice(1);
};

const getMoodScore = (item) => {
    const rawScore = item?.data?.score;
    if (typeof rawScore === 'number') {
        return rawScore;
    }
    if (typeof rawScore === 'string' && rawScore.trim() !== '') {
        const parsed = Number(rawScore);
        if (Number.isFinite(parsed)) {
            return parsed;
        }
    }
    return moodStateToScore(item?.data?.state);
};

const buildMoodChartData = (caregiverMeasurements = [], patientMeasurements = []) => {
    const grouped = new Map();

    const pushItems = (items, key) => {
        [...(items || [])]
            .forEach((item) => {
                const score = getMoodScore(item);
                const tsValue = item?.ts ? new Date(item.ts).getTime() : NaN;

                if (!Number.isFinite(score) || !Number.isFinite(tsValue)) {
                    return;
                }

                const current = grouped.get(tsValue) || {
                    ts: tsValue,
                    caregiver: null,
                    patient: null
                };
                current[key] = score;
                grouped.set(tsValue, current);
            });
    };

    pushItems(caregiverMeasurements, 'caregiver');
    pushItems(patientMeasurements, 'patient');

    return [...grouped.values()].sort((a, b) => a.ts - b.ts);
};

const formatChartDateLabel = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }

    return date.toLocaleDateString('pt-PT', {
        day: '2-digit',
        month: '2-digit'
    });
};

const MOOD_RANGE_OPTIONS = [
    { key: 7, label: '7 dias' },
    { key: 30, label: '30 dias' },
    { key: 60, label: '60 dias' }
];

const PSYCHOLOGICAL_STRESS_OPTIONS = [
    'Sem Sobrecarga',
    'Sobrecarga ligeira',
    'Sobrecarga intensa'
];

const resolveMoodRangeBounds = (
    caregiverMeasurements = [],
    patientMeasurements = [],
    days = 60
) => {
    const allMeasurements = [...(caregiverMeasurements || []), ...(patientMeasurements || [])];

    const latestTs = allMeasurements.reduce((maxTs, item) => {
        const tsValue = item?.ts ? new Date(item.ts).getTime() : NaN;
        return Number.isFinite(tsValue) ? Math.max(maxTs, tsValue) : maxTs;
    }, Number.NEGATIVE_INFINITY);

    if (!Number.isFinite(latestTs)) {
        return null;
    }

    return {
        latestTs,
        startTs: latestTs - (days - 1) * 24 * 60 * 60 * 1000
    };
};

const filterMoodMeasurementsByRange = (measurements = [], rangeBounds = null) => {
    if (!Array.isArray(measurements) || !measurements.length) {
        return [];
    }

    if (!rangeBounds) {
        return [];
    }

    return measurements.filter((item) => {
        const tsValue = item?.ts ? new Date(item.ts).getTime() : NaN;
        return (
            Number.isFinite(tsValue) &&
            tsValue >= rangeBounds.startTs &&
            tsValue <= rangeBounds.latestTs
        );
    });
};

const moodEmojiAssetMap = {
    1: '/assets/emojis-svg/mood_1.svg',
    2: '/assets/emojis-svg/mood_2.svg',
    3: '/assets/emojis-svg/mood_3.svg',
    4: '/assets/emojis-svg/mood_4.svg',
    5: '/assets/emojis-svg/mood_5.svg',
    6: '/assets/emojis-svg/mood_6.svg',
    7: '/assets/emojis-svg/mood_7.svg'
};

const MoodYAxisTick = ({ x, y, payload }) => {
    const emojiSrc = moodEmojiAssetMap[payload?.value];

    if (!emojiSrc) {
        return null;
    }

    return (
        <g transform={`translate(${x},${y})`}>
            <image
                href={emojiSrc}
                x={-18}
                y={-9}
                width={18}
                height={18}
                preserveAspectRatio="xMidYMid meet"
            />
        </g>
    );
};

const MoodFace = ({ score }) => {
    const numericScore = Number.isFinite(score) ? Math.max(1, Math.min(7, Math.round(score))) : null;
    const emojiSrc = numericScore ? moodEmojiAssetMap[numericScore] : null;

    if (!emojiSrc) {
        return null;
    }

    return (
        <Box
            component="img"
            src={emojiSrc}
            alt=""
            aria-hidden="true"
            sx={{
                width: 96,
                height: 96,
                objectFit: 'contain',
                display: 'block'
            }}
        />
    );
};

const EmptyEvaluationBlock = ({ score = null, label = 'Sem avaliações', timestamp = null }) => (
    <Paper sx={{ ...softInnerCardSx, mt: 2 }}>
        <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1, ...sectionTitleSx }}
        >
            <Typography fontWeight={700}>Últimas Avaliações</Typography>
            <Typography fontWeight={700}>{formatEvaluationStamp(timestamp)}</Typography>
        </Stack>
        <Box
            sx={{
                minHeight: 98,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: 1
            }}
        >
            {score == null ? (
                <Typography variant="body2" color="text.secondary">
                    {label}
                </Typography>
            ) : (
                <>
                    <MoodFace score={score} />
                    <Typography variant="body2" color="text.secondary" fontWeight={600}>
                        {label}
                    </Typography>
                </>
            )}
        </Box>
    </Paper>
);

const UpcomingAppointmentsSection = ({
    appointments = [],
    loading = false,
    onCreateAppointmentClick,
    onRescheduleAppointment,
    onStartAppointmentCall,
    onCancelAppointment
}) => (
    <Paper sx={cardSx}>
        <Typography fontWeight={700} sx={{ mb: 2, ...sectionTitleSx }}>
            Próximas Marcações
        </Typography>

        <Tooltip title="Criar novo agendamento">
            <Button
                fullWidth
                variant="outlined"
                startIcon={<AddCircleRoundedIcon />}
                onClick={onCreateAppointmentClick}
                sx={{
                    minHeight: 44,
                    mb: 1.8,
                    borderRadius: '12px',
                    borderStyle: 'dashed',
                    borderColor: '#BDBDBD',
                    color: '#6B6B6B',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                        borderStyle: 'dashed',
                        borderColor: '#D63731',
                        bgcolor: 'rgba(214, 55, 49, 0.04)'
                    }
                }}
            >
                Novo Agendamento
            </Button>
        </Tooltip>

        <Paper sx={appointmentHeaderSx}>
            <Typography variant="caption" fontWeight={600}>Data</Typography>
            <Typography variant="caption" fontWeight={600}>Hora</Typography>
            <Typography variant="caption" fontWeight={600}>Atendimento</Typography>
            <Typography variant="caption" fontWeight={600}>Equipa</Typography>
            <Typography variant="caption" fontWeight={600} textAlign="center">Ações</Typography>
        </Paper>

        <Stack spacing={1} mt={1}>
            {loading ? (
                <FullWidthMessageRow sx={appointmentRowSx} loading>
                    A carregar agendamentos...
                </FullWidthMessageRow>
            ) : null}

            {!loading && appointments.length === 0 ? (
                <FullWidthMessageRow sx={appointmentRowSx}>
                    Sem marcações futuras.
                </FullWidthMessageRow>
            ) : null}

            {!loading
                ? appointments.map((item) => (
                    <Paper key={item.uuid} sx={appointmentRowSx}>
                        <Box>
                            <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, lineHeight: 1, color: '#3D3D3D' }}>
                                {item.dayLabel}
                            </Typography>
                            <Typography sx={{ mt: 0.4, fontSize: '0.78rem', fontWeight: 600, color: '#6B6B6B', textTransform: 'capitalize' }}>
                                {item.weekdayLabel}
                            </Typography>
                        </Box>

                        <Typography variant="body2" fontWeight={600} color="#3D3D3D">
                            {item.timeLabel}
                        </Typography>

                        <Typography variant="body2" fontWeight={600} color="#3D3D3D">
                            {item.appointmentTypeLabel}
                        </Typography>

                        <Typography variant="body2" fontWeight={600} color="#3D3D3D">
                            {item.clinicianLabel}
                        </Typography>

                        <Stack direction="row" spacing={1} justifyContent="center">
                            <Tooltip title="Cancelar agendamento">
                                <IconButton
                                    size="small"
                                    sx={{
                                        ...actionButtonSx,
                                        justifySelf: 'center',
                                        bgcolor: '#FFFFFF',
                                        color: '#3D3D3D',
                                        border: '1.5px solid #3D3D3D',
                                        '&:hover': {
                                            bgcolor: '#F7F7F7',
                                            borderColor: '#3D3D3D'
                                        }
                                    }}
                                    onClick={() => onCancelAppointment?.(item)}
                                >
                                    <CloseOutlinedIcon sx={{ fontSize: 18, color: '#3D3D3D' }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Reagendar agendamento">
                                <IconButton
                                    size="small"
                                    sx={{ ...actionButtonSx, justifySelf: 'center' }}
                                    onClick={() => onRescheduleAppointment?.(item)}
                                >
                                    <Box
                                        component="img"
                                        src="/assets/logos-svg/reschedule.svg"
                                        alt="Reagendar"
                                        sx={{ width: 18, height: 18, display: 'block' }}
                                    />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={item.canCall ? 'Iniciar videochamada do agendamento' : 'Videochamada indisponível'}>
                                <span>
                                    <IconButton
                                        size="small"
                                        sx={{
                                            ...actionButtonSx,
                                            bgcolor: item.canCall ? '#E91C24' : '#FFFFFF',
                                            '&:hover': { bgcolor: item.canCall ? '#CF1820' : '#FFFFFF' },
                                            '&.Mui-disabled': {
                                                bgcolor: '#FFFFFF',
                                                color: '#3D3D3D',
                                                opacity: 1,
                                                border: '1.5px solid #D9D9D9'
                                            }
                                        }}
                                        disabled={!item.canCall}
                                        onClick={() => onStartAppointmentCall?.(item)}
                                    >
                                        <Box
                                            component="img"
                                            src={item.canCall ? '/assets/logos-svg/call.svg' : '/assets/logos-svg/callBlack.svg'}
                                            alt="Videochamada"
                                            sx={{ width: 18, height: 18, display: 'block' }}
                                        />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Stack>
                    </Paper>
                ))
                : null}
        </Stack>
    </Paper>
);

const FullWidthMessageRow = ({ sx, loading, children }) => (
    <Paper sx={sx}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ gridColumn: '1 / -1' }}>
            {loading ? <CircularProgress size={16} /> : null}
            <Typography variant="body2" color="text.secondary" textAlign="center" fontWeight={600}>
                {children}
            </Typography>
        </Stack>
    </Paper>
);

const LatestNotesTable = ({ note, loading, onOpenNotes }) => (
    <Box>
        <Paper sx={noteTableHeaderSx}>
            <Typography variant="caption" fontWeight={600} textAlign="center">Avaliador</Typography>
            <Typography variant="caption" fontWeight={600} textAlign="center">Data</Typography>
            <Typography variant="caption" fontWeight={600} textAlign="center">Tipo</Typography>
            <Typography variant="caption" fontWeight={600} textAlign="center">Notas</Typography>
        </Paper>

        {loading ? (
            <FullWidthMessageRow sx={{ ...noteTableRowSx, mt: 0.8 }} loading>
                A carregar...
            </FullWidthMessageRow>
        ) : null}

        {!loading && !note ? (
            <FullWidthMessageRow sx={{ ...noteTableRowSx, mt: 0.8 }}>
                Sem notas disponiveis.
            </FullWidthMessageRow>
        ) : null}

        {!loading && note ? (
            <Paper sx={{ ...noteTableRowSx, mt: 0.8 }}>
                <Typography variant="body2" fontWeight={700} color="#3D3D3D" textAlign="center">
                    {note?.author?.name || 'Sistema'}
                </Typography>
                <Typography variant="body2" fontWeight={600} color="text.secondary" textAlign="center">
                    {toPtDateTime(note?.ts || note?.created)}
                </Typography>
                <Typography variant="body2" fontWeight={600} color="text.secondary" textAlign="center">
                    {note?.appointment ? 'Consulta associada' : 'Nota manual'}
                </Typography>
                <Tooltip title="Ver nota">
                    <IconButton size="small" sx={{ ...actionButtonSx, justifySelf: 'center' }} onClick={() => onOpenNotes?.(note?.uuid)}>
                        <AssignmentOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Paper>
        ) : null}
    </Box>
);

const LatestMeasurementTable = ({ measurement, loading }) => (
    <Box>
        <Paper sx={metricTableHeaderSx}>
            <Typography variant="caption" fontWeight={600}>TA Sist.</Typography>
            <Typography variant="caption" fontWeight={600}>TA Diast.</Typography>
            <Typography variant="caption" fontWeight={600}>Glicose</Typography>
            <Typography variant="caption" fontWeight={600}>Temp</Typography>
            <Typography variant="caption" fontWeight={600}>FC</Typography>
            <Typography variant="caption" fontWeight={600}>SpO2</Typography>
            <Typography variant="caption" fontWeight={600}>Peso</Typography>
            <Typography variant="caption" fontWeight={600}>Exercicio</Typography>
        </Paper>

        {loading ? (
            <FullWidthMessageRow sx={{ ...metricTableRowSx, mt: 0.8 }} loading>
                A carregar medicoes...
            </FullWidthMessageRow>
        ) : null}

        {!loading && !measurement ? (
            <FullWidthMessageRow sx={{ ...metricTableRowSx, mt: 0.8 }}>
                Sem registos de monitorizacao.
            </FullWidthMessageRow>
        ) : null}

        {!loading && measurement ? (
            <Paper sx={{ ...metricTableRowSx, mt: 0.8 }}>
                <Stack spacing={0.35} alignItems="center"><Typography variant="body2" fontWeight={700} color="#3D3D3D" textAlign="center">{measurement.bpSystolic}</Typography><Typography variant="caption" color="text.secondary" textAlign="center">{measurement.bpDate}</Typography></Stack>
                <Stack spacing={0.35} alignItems="center"><Typography variant="body2" fontWeight={700} color="#3D3D3D" textAlign="center">{measurement.bpDiastolic}</Typography><Typography variant="caption" color="text.secondary" textAlign="center">{measurement.bpDate}</Typography></Stack>
                <Stack spacing={0.35} alignItems="center"><Typography variant="body2" fontWeight={700} color="#3D3D3D" textAlign="center">{measurement.glucose}</Typography><Typography variant="caption" color="text.secondary" textAlign="center">{measurement.glucoseDate}</Typography></Stack>
                <Stack spacing={0.35} alignItems="center"><Typography variant="body2" fontWeight={700} color="#3D3D3D" textAlign="center">{measurement.temperature}</Typography><Typography variant="caption" color="text.secondary" textAlign="center">{measurement.temperatureDate}</Typography></Stack>
                <Stack spacing={0.35} alignItems="center"><Typography variant="body2" fontWeight={700} color="#3D3D3D" textAlign="center">{measurement.hr}</Typography><Typography variant="caption" color="text.secondary" textAlign="center">{measurement.hrDate}</Typography></Stack>
                <Stack spacing={0.35} alignItems="center"><Typography variant="body2" fontWeight={700} color="#3D3D3D" textAlign="center">{measurement.oxygen}</Typography><Typography variant="caption" color="text.secondary" textAlign="center">{measurement.oxygenDate}</Typography></Stack>
                <Stack spacing={0.35} alignItems="center"><Typography variant="body2" fontWeight={700} color="#3D3D3D" textAlign="center">{measurement.weight}</Typography><Typography variant="caption" color="text.secondary" textAlign="center">{measurement.weightDate}</Typography></Stack>
                <Stack spacing={0.35} alignItems="center"><Typography variant="body2" fontWeight={700} color="#3D3D3D" textAlign="center">{measurement.exercise}</Typography><Typography variant="caption" color="text.secondary" textAlign="center">{measurement.exerciseDate}</Typography></Stack>
            </Paper>
        ) : null}
    </Box>
);

const StatusCard = ({
    title,
    timestampLabel,
    timestampText,
    helperText,
    overloadText,
    overloadTs = '',
    subtitle,
    children,
    rightFieldLabel = 'Niveis de Sobrecarga',
    rightFieldEditable = false,
    rightFieldLoading = false,
    rightFieldSaving = false,
    rightFieldPlaceholder = '',
    showRightField = true,
    reserveRightFieldSpace = false,
    rightFieldOptions = [],
    onRightFieldChange,
    onRightFieldSave,
    moodScore = null,
    moodLabel = 'Sem avaliacoes',
    latestMoodTs = null
}) => {
    const [localRightFieldValue, setLocalRightFieldValue] = useState(overloadText || '');
    const [isEditingRightField, setIsEditingRightField] = useState(false);

    useEffect(() => {
        setLocalRightFieldValue(overloadText || '');
    }, [overloadText]);

    useEffect(() => {
        if (!rightFieldEditable) {
            setIsEditingRightField(false);
        }
    }, [rightFieldEditable]);

    const rightFieldDateLabel = useMemo(() => {
        if (!overloadTs) {
            return '';
        }

        const date = new Date(overloadTs);

        if (Number.isNaN(date.getTime())) {
            return '';
        }

        return date.toLocaleDateString('pt-PT');
    }, [overloadTs]);

    return (
        <Paper variant="elevation=0" sx={cardSx}>
            <Typography variant="h4" fontWeight={700} sx={{ mb: 2, ...sectionTitleSx }}>
                {title}
            </Typography>

            <Stack spacing={2}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 1.8 }}>
                    <Paper sx={{ p: 1.25, minHeight: 145, borderRadius: '14px', bgcolor: '#FFFFFF', border: '1px solid #ECECEC', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: 'none' }}>
                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F7F7F7', borderRadius: '12px' }}>
                            <ImageOutlinedIcon sx={{ fontSize: 46, color: '#5D5D5D' }} />
                        </Box>
                        <Typography variant="body2" fontWeight={700} mt={0.75} color="#3D3D3D">
                            {subtitle || '--'}
                        </Typography>
                    </Paper>

                    <Stack spacing={1.25}>
                        <Paper sx={{ ...softInnerCardSx, minHeight: 80 }}>
                            <Typography variant="caption" color="text.secondary">
                                {timestampLabel}
                            </Typography>
                            <Box mt={0.6}>
                                <Typography variant="body2" fontWeight={600} color="text.secondary">
                                    {timestampText}
                                </Typography>
                                {helperText ? (
                                    <Typography variant="caption" color="text.secondary" mt={0.35} display="block">
                                        {helperText}
                                    </Typography>
                                ) : null}
                            </Box>
                        </Paper>

                        {showRightField ? (
                            rightFieldEditable ? (
                                <Stack spacing={0.7}>
                                    <Stack
                                        direction={{ xs: 'column', md: 'row' }}
                                        spacing={1}
                                        alignItems={{ xs: 'stretch', md: 'flex-end' }}
                                    >
                                        <TextField
                                            label={rightFieldLabel}
                                            value={localRightFieldValue}
                                            fullWidth
                                            placeholder={rightFieldPlaceholder}
                                            sx={{ ...panelInputSx, flex: 1 }}
                                            disabled={
                                                rightFieldLoading ||
                                                rightFieldSaving ||
                                                !isEditingRightField
                                            }
                                            select={isEditingRightField && rightFieldOptions.length > 0}
                                            onChange={(event) => {
                                                const nextValue = event.target.value;
                                                setLocalRightFieldValue(nextValue);
                                                onRightFieldChange?.(nextValue);
                                            }}
                                        >
                                            {isEditingRightField && rightFieldOptions.length > 0
                                                ? rightFieldOptions.map((option) => (
                                                    <MenuItem key={option} value={option}>
                                                        {option}
                                                    </MenuItem>
                                                ))
                                                : null}
                                        </TextField>
                                        <Button
                                            variant="contained"
                                            onClick={async () => {
                                                if (isEditingRightField) {
                                                    try {
                                                        await onRightFieldSave?.(localRightFieldValue);
                                                        setIsEditingRightField(false);
                                                    } catch {
                                                        // Parent component already handles the error state.
                                                    }
                                                    return;
                                                }

                                                setIsEditingRightField(true);
                                            }}
                                            disabled={rightFieldLoading || rightFieldSaving}
                                            startIcon={
                                                isEditingRightField ? (
                                                    <SaveOutlinedIcon sx={{ fontSize: 18 }} />
                                                ) : (
                                                    <Box
                                                        component="img"
                                                        src="/assets/logos-svg/edit.svg"
                                                        alt="Editar"
                                                        sx={{ width: 16, height: 16, display: 'block' }}
                                                    />
                                                )
                                            }
                                            sx={{
                                                minWidth: { xs: '100%', md: 122 },
                                                minHeight: 56,
                                                px: 2,
                                                borderRadius: '12px',
                                                textTransform: 'none',
                                                fontWeight: 700,
                                                bgcolor: '#D63731',
                                                '&:hover': {
                                                    bgcolor: '#C72E29'
                                                }
                                            }}
                                        >
                                            {rightFieldSaving
                                                ? 'A guardar...'
                                                : isEditingRightField
                                                  ? 'Guardar'
                                                  : 'Editar'}
                                        </Button>
                                    </Stack>

                                    {rightFieldDateLabel ? (
                                        <Typography variant="caption" color="text.secondary">
                                            Última avaliação: {rightFieldDateLabel}
                                        </Typography>
                                    ) : null}
                                </Stack>
                            ) : (
                                <TextField label={rightFieldLabel} value={overloadText} fullWidth disabled sx={panelInputSx} />
                            )
                        ) : reserveRightFieldSpace ? <Box sx={{ minHeight: 84 }} /> : null}
                    </Stack>
                </Box>

                {children}

                <EmptyEvaluationBlock score={moodScore} label={moodLabel} timestamp={latestMoodTs} />
            </Stack>
        </Paper>
    );
};

const EvolutionCard = ({ caregiverMoodMeasurements = [], patientMoodMeasurements = [] }) => {
    const [selectedRangeDays, setSelectedRangeDays] = useState(60);
    const rangeBounds = useMemo(
        () =>
            resolveMoodRangeBounds(
                caregiverMoodMeasurements,
                patientMoodMeasurements,
                selectedRangeDays
            ),
        [caregiverMoodMeasurements, patientMoodMeasurements, selectedRangeDays]
    );
    const filteredCaregiverMeasurements = useMemo(
        () => filterMoodMeasurementsByRange(caregiverMoodMeasurements, rangeBounds),
        [caregiverMoodMeasurements, rangeBounds]
    );
    const filteredPatientMeasurements = useMemo(
        () => filterMoodMeasurementsByRange(patientMoodMeasurements, rangeBounds),
        [patientMoodMeasurements, rangeBounds]
    );
    const chartData = useMemo(
        () => buildMoodChartData(filteredCaregiverMeasurements, filteredPatientMeasurements),
        [filteredCaregiverMeasurements, filteredPatientMeasurements]
    );

    return (
        <Paper sx={cardSx}>
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                spacing={1.4}
                sx={{ mb: 2 }}
            >
                <Typography fontWeight={700} sx={sectionTitleSx}>
                    Gráfico Evolutivo
                </Typography>
                <Stack direction="row" spacing={0.9} useFlexGap flexWrap="wrap">
                    {MOOD_RANGE_OPTIONS.map((option) => {
                        const selected = option.key === selectedRangeDays;

                        return (
                            <Button
                                key={option.key}
                                variant="outlined"
                                onClick={() => setSelectedRangeDays(option.key)}
                                sx={{
                                    minWidth: 0,
                                    minHeight: 34,
                                    px: 1.4,
                                    borderRadius: '999px',
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    fontSize: '0.82rem',
                                    color: selected ? '#D63731' : '#5F5F5F',
                                    borderColor: selected ? '#D63731' : '#D9D9D9',
                                    bgcolor: selected ? 'rgba(214, 55, 49, 0.06)' : '#FFFFFF',
                                    '&:hover': {
                                        borderColor: selected ? '#D63731' : '#C9C9C9',
                                        bgcolor: selected ? 'rgba(214, 55, 49, 0.09)' : '#F7F7F7'
                                    }
                                }}
                            >
                                {option.label}
                            </Button>
                        );
                    })}
                </Stack>
            </Stack>
            <Box sx={{ width: '100%', height: 180, mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F7F7F7', borderRadius: '14px' }}>
                {chartData.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 14, right: 12, left: -6, bottom: 8 }}>
                            <CartesianGrid stroke="#E7E7E7" strokeDasharray="3 3" vertical={false} />
                            <XAxis
                                type="number"
                                dataKey="ts"
                                domain={
                                    rangeBounds
                                        ? [rangeBounds.startTs, rangeBounds.latestTs]
                                        : ['dataMin', 'dataMax']
                                }
                                tickFormatter={formatChartDateLabel}
                                tick={{ fill: '#7A7A7A', fontSize: 11 }}
                                axisLine={false}
                                tickLine={false}
                                height={28}
                                tickCount={selectedRangeDays <= 7 ? 7 : selectedRangeDays <= 30 ? 6 : 8}
                                allowDuplicatedCategory={false}
                            />
                            <YAxis
                                domain={[1, 7]}
                                ticks={[1, 2, 3, 4, 5, 6, 7]}
                                tick={<MoodYAxisTick />}
                                axisLine={false}
                                tickLine={false}
                                padding={{ top: 6, bottom: 10 }}
                                interval={0}
                                allowDecimals={false}
                                width={42}
                            />
                            <RechartsTooltip
                                labelFormatter={(value) =>
                                    new Date(value).toLocaleString('pt-PT')
                                }
                                contentStyle={{
                                    borderRadius: 12,
                                    border: '1px solid #E4E4E4',
                                    boxShadow: '0px 8px 20px rgba(61, 61, 61, 0.08)'
                                }}
                            />
                            <Line type="monotone" dataKey="caregiver" stroke="#D63731" strokeWidth={2.5} dot={{ r: 3, fill: '#D63731' }} connectNulls />
                            <Line type="monotone" dataKey="patient" stroke="#F2C94C" strokeWidth={2.5} dot={{ r: 3, fill: '#F2C94C' }} connectNulls />
                        </LineChart>
                    </ResponsiveContainer>
                ) : (
                    <Typography variant="body2" color="text.secondary">Sem dados</Typography>
                )}
            </Box>
        </Paper>
    );
};

const CaregiverGeneralSection = ({
    vm,
    caregiver,
    patientName,
    notes = [],
    loadingNotes = false,
    onOpenNotes,
    latestMeasurementSummary = null,
    loadingMeasurements = false,
    caregiverMoodMeasurements = [],
    patientMoodMeasurements = [],
    psychologicalStressValue = '',
    psychologicalStressTs = '',
    loadingPsychologicalStress = false,
    savingPsychologicalStress = false,
    onSavePsychologicalStress,
    upcomingAppointments = [],
    loadingUpcomingAppointments = false,
    onCreateAppointmentClick,
    onRescheduleAppointment,
    onCancelAppointment,
    onStartAppointmentCall
}) => {
    const latestNote = notes?.[0] || null;
    const latestMeasurement = latestMeasurementSummary;

    const caregiverLastContact = caregiver?.last_call_ping
        ? toPtDateTime(caregiver.last_call_ping)
        : 'Sem contacto recente';

    const patientLastMonitoring = latestMeasurement?.date || 'Sem monitoramento registado';

    const caregiverMoodRows = caregiverMoodMeasurements?.length
        ? caregiverMoodMeasurements
        : (patientMoodMeasurements || []).filter(
            (item) => normalizeMoodState(item?.data?.target) === 'caregiver'
        );

    const patientMoodRows = (patientMoodMeasurements || []).filter((item) => {
        const target = normalizeMoodState(item?.data?.target);
        return !target || target === 'patient';
    });

    const latestCaregiverMood = caregiverMoodRows[0] || null;
    const latestPatientMood = patientMoodRows[0] || null;

    return (
        <Stack spacing={3}>
            <UpcomingAppointmentsSection
                appointments={upcomingAppointments}
                loading={loadingUpcomingAppointments}
                onCreateAppointmentClick={onCreateAppointmentClick}
                onRescheduleAppointment={onRescheduleAppointment}
                onCancelAppointment={onCancelAppointment}
                onStartAppointmentCall={onStartAppointmentCall}
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 2.5, alignItems: 'stretch' }}>
                <StatusCard
                    title="Cuidador"
                    subtitle={vm.name}
                    timestampLabel="Último Contacto"
                    timestampText={caregiverLastContact}
                    helperText=""
                    overloadText={psychologicalStressValue}
                    overloadTs={psychologicalStressTs}
                    rightFieldLabel="Nível de Sobrecarga"
                    rightFieldEditable
                    rightFieldLoading={loadingPsychologicalStress}
                    rightFieldSaving={savingPsychologicalStress}
                    rightFieldPlaceholder="Registar avaliacao tecnica"
                    rightFieldOptions={PSYCHOLOGICAL_STRESS_OPTIONS}
                    onRightFieldSave={onSavePsychologicalStress}
                    moodScore={getMoodScore(latestCaregiverMood)}
                    moodLabel={formatMoodStateLabel(latestCaregiverMood?.data?.state)}
                    latestMoodTs={latestCaregiverMood?.ts || null}
                >
                    <LatestNotesTable note={latestNote} loading={loadingNotes} onOpenNotes={onOpenNotes} />
                </StatusCard>

                <StatusCard
                    title="Paciente"
                    subtitle={patientName || 'Sem paciente associado'}
                    timestampLabel="Última Monitorização"
                    timestampText={patientLastMonitoring}
                    overloadText=""
                    showRightField={false}
                    reserveRightFieldSpace
                    moodScore={getMoodScore(latestPatientMood)}
                    moodLabel={formatMoodStateLabel(latestPatientMood?.data?.state)}
                    latestMoodTs={latestPatientMood?.ts || null}
                >
                    <LatestMeasurementTable measurement={latestMeasurement} loading={loadingMeasurements} />
                </StatusCard>
            </Box>

            <EvolutionCard caregiverMoodMeasurements={caregiverMoodRows} patientMoodMeasurements={patientMoodRows} />
        </Stack>
    );
};

export default CaregiverGeneralSection;



